/**
 * Artifact citations a returned message can name inline (`@[assetList:<id>]` and the same for
 * `boardList` / `changePreview` / `table`). Membership stays on the artifact, not in `metadata.pills`.
 * `searchFilter` and `unstructured` are on the artifact API and have no message chip yet, so a
 * moniker of those types stays prose.
 *
 * `@[asset:<assetId>]`, `@[assetVersion:<versionId>]`, `@[board:<boardId>]`,
 * `@[comment:<commentId>]`, `@[shareLink:<shortId>]`, `@[showcase:<collectionId>]`, and `@[contentExporter:<taskId>]` name
 * the entity itself. The client loads that asset, version, board, comment, share link, or showcase directly;
 * `contentExporter` mints a signed download URL on click instead.
 */
export const WORKSPACE_AGENT_ARTIFACT_PILL_CITATION_TYPES = [
  'assetList',
  'boardList',
  'changePreview',
  'table',
] as const;

export type WorkspaceAgentArtifactPillCitationType = (typeof WORKSPACE_AGENT_ARTIFACT_PILL_CITATION_TYPES)[number];

/** Entity id in the moniker, not a session artifact id. `assetVersion` must stay ahead of `asset` in the scanner. */
export const WORKSPACE_AGENT_ENTITY_MONIKER_TYPES = [
  'assetVersion',
  'asset',
  'board',
  'comment',
  'skill',
  'contextSourceFieldValue',
  'shareLink',
  'showcase',
  'contentExporter',
] as const;

export type WorkspaceAgentEntityMonikerType = (typeof WORKSPACE_AGENT_ENTITY_MONIKER_TYPES)[number];

const ARTIFACT_PILL_CITATION_TYPES = new Set<string>(WORKSPACE_AGENT_ARTIFACT_PILL_CITATION_TYPES);
const ENTITY_MONIKER_TYPES = new Set<string>(WORKSPACE_AGENT_ENTITY_MONIKER_TYPES);

export const isWorkspaceAgentArtifactPillCitationType = (
  value: string,
): value is WorkspaceAgentArtifactPillCitationType => ARTIFACT_PILL_CITATION_TYPES.has(value);

export const isWorkspaceAgentEntityMonikerType = (value: string): value is WorkspaceAgentEntityMonikerType =>
  ENTITY_MONIKER_TYPES.has(value);

/** Valid `@[type:id]` monikers that intentionally stay prose until a chip exists. */
export const WORKSPACE_AGENT_MONIKER_TYPES_WITHOUT_CHIP = ['searchFilter', 'unstructured'] as const;

const RECOGNIZED_MONIKER_TYPES = new Set<string>([
  ...WORKSPACE_AGENT_ARTIFACT_PILL_CITATION_TYPES,
  ...WORKSPACE_AGENT_ENTITY_MONIKER_TYPES,
  'pill',
]);

const MONIKER_TYPES_WITHOUT_CHIP = new Set<string>(WORKSPACE_AGENT_MONIKER_TYPES_WITHOUT_CHIP);

export const isWorkspaceAgentRecognizedMonikerType = (value: string): boolean => RECOGNIZED_MONIKER_TYPES.has(value);

/** Valid moniker shape the frontend does not render as a chip (refresh may be needed after deploy). */
export const isWorkspaceAgentUnknownMonikerType = (value: string): boolean =>
  !isWorkspaceAgentRecognizedMonikerType(value) && !MONIKER_TYPES_WITHOUT_CHIP.has(value);
